import { useState } from 'react';
import { Button, Card, CardBody, Chip } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import ContactModal from '../../../components/feature/ContactModal';

type ServiceContent = {
  category: string;
  title: string;
  description: string;
  useCases: string[];
  cta: string;
};

type Service = ServiceContent & {
  icon: string;
};

const serviceIcons = ['ri-window-line', 'ri-shopping-bag-3-line', 'ri-settings-3-line'] as const;

export default function Features() {
  const { t } = useTranslation();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const services = (t('home.features.services', { returnObjects: true }) as ServiceContent[]).map(
    (service, index) => ({
      ...service,
      icon: serviceIcons[index] ?? 'ri-flashlight-line',
    })
  );

  const openServiceModal = (serviceTitle?: string) => {
    setSelectedService(serviceTitle ?? null);
    setIsContactModalOpen(true);
  };

  return (
    <>
      <section
        id="sluzby"
        className="relative scroll-mt-28 overflow-hidden bg-gradient-to-b from-brand-ocean via-brand-beige-light/80 to-brand-ocean"
      >
        <div className="pointer-events-none absolute -left-20 top-24 h-64 w-64 rounded-full bg-brand-green/10 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 bottom-40 h-72 w-72 rounded-full bg-brand-ocean/10 blur-3xl" />

        <div className="relative z-10 mx-auto max-w-7xl px-6 py-24 lg:py-28">
          <div className="mx-auto mb-16 max-w-3xl text-center lg:mb-20">
            <Chip
              data-services-badge
              variant="flat"
              className="mb-5 border border-brand-green/20 bg-white/90 px-4 py-2 text-sm font-semibold text-brand-green"
              startContent={
                <span className="mr-2 inline-flex items-center">
                  <i className="ri-flashlight-line text-base" />
                </span>
              }
            >
              {t('home.features.badge')}
            </Chip>
            <h2
              data-services-title
              className="mb-6 text-4xl font-caprasimo text-brand-black md:text-6xl"
            >
              {t('home.features.title')}
            </h2>
            <p data-services-copy className="text-lg leading-relaxed text-brand-grey md:text-xl">
              {t('home.features.description')}
            </p>
          </div>

          <div data-services-grid className="grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((service) => (
              <Card
                key={service.title}
                data-service-card
                className="group relative h-full overflow-hidden rounded-3xl border border-brand-beige bg-[#1A1A1A] p-7 shadow-[0_15px_40px_rgba(26,26,26,0.06)]"
              >
                <div className="absolute right-0 top-0 h-32 w-32 -translate-y-8 translate-x-8 rounded-full bg-brand-green/10 blur-2xl" />

                <CardBody className="relative z-10 flex h-full flex-col p-7">
                  <div className="mb-6 flex items-start justify-between gap-4">
                    <Chip
                      variant="flat"
                      className="bg-brand-beige-light px-4 py-2 text-sm font-semibold text-brand-black"
                      startContent={
                        <span className="mr-2 inline-flex items-center">
                          <i className={`${service.icon} text-lg text-brand-green`} />
                        </span>
                      }
                    >
                      {service.category}
                    </Chip>
                  </div>

                  <h3 className="mb-3 text-2xl font-caprasimo leading-tight text-brand-black md:text-3xl">
                    {service.title}
                  </h3>
                  <p className="mb-6 text-brand-grey">{service.description}</p>

                  <p className="mb-3 text-sm font-semibold uppercase tracking-[0.12em] text-brand-green">
                    {t('home.features.useCasesTitle')}
                  </p>
                  <ul className="mb-7 flex-1 space-y-3">
                    {service.useCases.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-brand-grey">
                        <span className="mt-1 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-brand-green/15">
                          <i className="ri-check-line text-xs text-brand-green" />
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    type="button"
                    onPress={() => openServiceModal(service.title)}
                    className="mt-auto inline-flex h-auto w-full justify-between rounded-[18px] border border-white/18 bg-gradient-to-r from-brand-green via-[#b4ec4b] to-[#c8f06d] px-5 py-3.5 text-[0.96rem] font-black text-[#0D0D0D] shadow-[0_12px_24px_rgba(163,230,53,0.22)] transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_30px_rgba(163,230,53,0.28)]"
                    endContent={
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-black/10 text-[#0D0D0D] shadow-[inset_0_1px_0_rgba(255,255,255,0.22)]">
                        <i className="ri-arrow-right-line text-base" />
                      </span>
                    }
                  >
                    {service.cta}
                  </Button>
                </CardBody>
              </Card>
            ))}
          </div>

          <Card
            data-services-bottom-cta
            className="group relative mt-12 overflow-hidden rounded-3xl border border-brand-beige bg-[#1A1A1A] shadow-[0_15px_40px_rgba(26,26,26,0.06)]"
          >
            <div className="absolute right-0 top-0 h-36 w-36 -translate-y-10 translate-x-10 rounded-full bg-brand-green/10 blur-2xl" />
            <CardBody className="relative z-10 flex flex-col gap-7 p-8 md:p-10 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-3xl">
                <Chip
                  variant="flat"
                  className="mb-5 bg-brand-beige-light px-4 py-2 text-sm font-semibold text-brand-black"
                  startContent={
                    <span className="mr-2 inline-flex items-center">
                      <i className="ri-question-line text-base text-brand-green" />
                    </span>
                  }
                >
                  {t('home.features.bottom.badge')}
                </Chip>
                <h3 className="mb-3 text-2xl font-caprasimo leading-tight text-brand-black md:text-3xl">
                  {t('home.features.bottom.title')}
                </h3>
                <p className="text-lg leading-relaxed text-brand-grey">
                  {t('home.features.bottom.description')}
                </p>
              </div>
              <Button
                type="button"
                onPress={() => openServiceModal()}
                className="inline-flex h-auto min-w-[240px] justify-between rounded-[18px] border border-white/18 bg-gradient-to-r from-brand-green via-[#b4ec4b] to-[#c8f06d] px-5 py-3.5 text-[0.96rem] font-black text-[#0D0D0D] shadow-[0_12px_24px_rgba(163,230,53,0.22)] transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_30px_rgba(163,230,53,0.28)]"
                endContent={
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-black/10 text-[#0D0D0D] shadow-[inset_0_1px_0_rgba(255,255,255,0.22)]">
                    <i className="ri-phone-line text-base" />
                  </span>
                }
              >
                {t('home.features.bottom.cta')}
              </Button>
            </CardBody>
          </Card>
        </div>
      </section>

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        presetService={selectedService}
      />
    </>
  );
}
